import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Button, Input, Label } from '../components/ui'
import { Building2, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'

interface InvitationDetails {
  id: string
  team_name: string
  inviter_name: string
  role: string
  contact: string
  contact_type: 'email' | 'phone'
  expires_at: number
}

export default function Signup() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [invitationLoading, setInvitationLoading] = useState(false)

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard')
    }
  }, [user, authLoading, navigate])

  // Redirect to login if coming from invitation (no password signup for invitations)
  useEffect(() => {
    if (inviteToken && !authLoading) {
      navigate(`/login?invite=${inviteToken}`)
    }
  }, [inviteToken, authLoading, navigate])

  // Load invitation details if invite token is present
  useEffect(() => {
    if (inviteToken) {
      loadInvitationDetails()
    }
  }, [inviteToken])

  const loadInvitationDetails = async () => {
    if (!inviteToken) return

    try {
      setInvitationLoading(true)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${inviteToken}`
      )

      if (response.ok) {
        const data = await response.json()
        setInvitation(data.invitation)
        
        // Pre-fill email if invitation is for email
        if (data.invitation.contact_type === 'email') {
          setFormData(prev => ({ ...prev, email: data.invitation.contact }))
        }
      }
    } catch (err) {
      console.error('Failed to load invitation:', err)
    } finally {
      setInvitationLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }
    
    if (!formData.email.trim()) {
      setError('Email is required')
      return
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Register the user
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/auth/email/register`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            email: formData.email.trim().toLowerCase(),
            password: formData.password
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Registration failed')
      }

      const authData = await response.json()
      
      // Store the auth token
      localStorage.setItem('auth_token', authData.token)

      // If there's an invitation, accept it automatically
      if (inviteToken) {
        try {
          const acceptResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${inviteToken}/accept`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authData.token}`
              }
            }
          )

          if (acceptResponse.ok) {
            // Successful registration and invitation acceptance
            navigate('/dashboard?invitation_accepted=true')
            window.location.reload() // Refresh to update company context
            return
          } else {
            // Registration succeeded but invitation failed
            const inviteError = await acceptResponse.json()
            console.error('Failed to accept invitation:', inviteError)
            // Still redirect to dashboard, but show that invitation failed
            navigate('/dashboard?invite_error=true')
            return
          }
        } catch (inviteErr) {
          console.error('Error accepting invitation:', inviteErr)
          // Registration succeeded but invitation failed
          navigate('/dashboard?invite_error=true')
          return
        }
      }

      // No invitation - proceed to onboarding if needed
      if (authData.needsOnboarding) {
        navigate('/onboarding')
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      console.error('Signup error:', err)
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
    if (error) setError(null) // Clear error when user starts typing
  }

  if (authLoading || invitationLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {invitation ? `Join ${invitation.team_name}` : 'Create Your Account'}
            </h2>
            
            <p className="text-gray-600">
              {invitation 
                ? `Complete your registration to accept ${invitation.inviter_name}'s invitation`
                : 'Get started with ClockInNOut'
              }
            </p>
          </div>

          {/* Invitation Details */}
          {invitation && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">Team Invitation</span>
              </div>
              <div className="text-sm text-blue-800">
                <p><strong>Company:</strong> {invitation.team_name}</p>
                <p><strong>Role:</strong> {invitation.role}</p>
                <p><strong>Invited by:</strong> {invitation.inviter_name}</p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange('name')}
                className="mt-1"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                className="mt-1"
                placeholder="Enter your email"
                required
                disabled={invitation?.contact_type === 'email'} // Disable if pre-filled from invitation
              />
              {invitation?.contact_type === 'email' && (
                <p className="text-xs text-gray-500 mt-1">
                  Email is pre-filled from your invitation
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  className="pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="mt-1 relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  className="pr-10"
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to={inviteToken ? `/login?invite=${inviteToken}` : '/login'} 
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}